import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getAxiosErrorMessage } from "@/lib/api";
import { Link2, Plus, ArrowUpRight, CornerLeftUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DeriveComplaintDialog from "@/components/DeriveComplaintDialog";
import { TYPE_LABELS } from "@/lib/complaintTypes";
import ComplaintStatusBadge from "@/components/ComplaintStatusBadge";

// La table de statuts locale qui vivait ici divergeait de celle du badge
// commun : « ouverte » y était ambre et « clôturée » émeraude. Un même litige
// n'avait donc pas la même couleur selon l'écran. C'est la classe de bug qui
// avait déjà laissé partir un doublon de typologie en production.

function LigneReclamation({ item, onOpen, role }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      className="w-full text-left p-3 rounded-xl border border-slate-100 hover:border-blue-300 hover:bg-blue-50/40 transition-colors group"
      data-testid={`linked-${item.number}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
              {role}
            </span>
            <span className="font-semibold text-slate-900">{item.number}</span>
            <ArrowUpRight
              size={13}
              className="text-slate-300 group-hover:text-blue-600 transition-colors"
            />
          </div>
          <div className="text-sm text-slate-600 truncate mt-0.5">
            {TYPE_LABELS[item.type] || item.type}
            {item.tiers || item.supplier_name || item.carrier_name || item.client_name
              ? ` · ${item.tiers || item.supplier_name || item.carrier_name || item.client_name}`
              : ""}
          </div>
        </div>
        <ComplaintStatusBadge status={item.status} className="shrink-0" />
      </div>
    </button>
  );
}

/**
 * Litiges associés à un litige.
 *
 * Depuis un litige client : ses litiges fournisseur/transport dérivés.
 * Depuis un litige dérivé : le litige client d'origine.
 *
 * Les actions de dérivation n'apparaissent que si le backend les autorise —
 * `can_derive` vient de la matrice de permissions, l'interface ne la recopie pas.
 */
export default function LinkedComplaints({ complaintId, onChanged }) {
  const [liens, setLiens] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [derivation, setDerivation] = useState(null); // "fournisseur" | "transporteur"
  const navigate = useNavigate();

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get(`/complaints/${complaintId}/linked`);
      setLiens(data);
    } catch (e) {
      toast.error(getAxiosErrorMessage(e));
    } finally {
      setChargement(false);
    }
  }, [complaintId]);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = (id) => navigate(`/litiges/${id}`);

  if (chargement) {
    return (
      <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
      </section>
    );
  }
  if (!liens) return null;

  const { parent, children = [], can_derive: peutDeriver = {} } = liens;
  const actions = ["fournisseur", "transporteur"].filter((t) => peutDeriver[t]);
  const rienALier = !parent && children.length === 0;

  return (
    <section
      className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6"
      data-testid="linked-complaints"
    >
      <h3 className="text-lg font-semibold text-slate-900 font-heading mb-4 flex items-center gap-2">
        <Link2 size={18} strokeWidth={1.5} /> Litiges associés
      </h3>

      {parent ? (
        <div className="mb-3">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-1.5 flex items-center gap-1.5">
            <CornerLeftUp size={12} /> Litige d'origine
          </div>
          <LigneReclamation item={parent} onOpen={ouvrir} role="Origine" />
        </div>
      ) : null}

      {children.length > 0 ? (
        <div className="space-y-2">
          {children.map((c) => (
            <LigneReclamation key={c.id} item={c} onOpen={ouvrir} role="Dérivée" />
          ))}
        </div>
      ) : null}

      {rienALier ? (
        <p className="text-sm text-slate-400 py-2" data-testid="linked-empty">
          Aucun litige fournisseur ou transport associé.
        </p>
      ) : null}

      {actions.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2 pt-4 border-t border-slate-100">
          {actions.map((type) => (
            <Button
              key={type}
              type="button"
              variant="outline"
              className="rounded-xl h-9"
              onClick={() => setDerivation(type)}
              data-testid={`derive-${type}-btn`}
            >
              <Plus size={14} className="mr-1.5" />
              Créer un litige {TYPE_LABELS[type] || type}
            </Button>
          ))}
        </div>
      ) : null}

      {derivation ? (
        <DeriveComplaintDialog
          complaintId={complaintId}
          type={derivation}
          open={Boolean(derivation)}
          onOpenChange={(v) => { if (!v) setDerivation(null); }}
          onCreated={() => { setDerivation(null); charger(); onChanged?.(); }}
        />
      ) : null}
    </section>
  );
}
